import { Intent } from '@blueprintjs/core'
import axios from 'axios'
import EntryType, { PropertyType } from 'common/entryType'
import { FilterFields } from 'common/models'
import Toaster from 'components/Toaster'
import { FileItem } from 'components/ZipViewer/KLZipFileViewer'
import { get } from 'lodash'
import _ from 'lodash'
import { stringify } from 'query-string'
import { Dispatch } from 'redux'
import { addVideoHistoryEntryFlow } from 'store/history/actions'
import {
  getHistoryActionFromRegion,
  getHistoryTypeFromRegion,
} from 'store/history/selectors'
import { showModalFlow } from 'store/modal/actions'
import { getTimeRangeString } from 'utilities/datetime'
import { parseHTTPError } from 'utilities/error'

import {
  addVideoImportLog,
  archiveEncordAssets,
  batchUploadToDarwin,
  batchUploadToEncord,
  batchUploadToSuperAnnotate,
  bulkAddRegion,
  bulkAddTags,
  editVideoProperty,
  editVideoTag,
  extractFrames,
  ExtractFramesPayload,
  extractVideoFrames,
  fetchPublicVideoURL,
  fetchVideoBatchesWithFilter,
  fetchVideoSegmentsWithFilter,
  getFilterFields,
  getSpatialLabelingAPIToken,
  uploadDicomAsMP4,
  uploadDicomsToEncord,
  uploadDicomsToFlexView,
  uploadToDarwin,
  uploadToEncord,
  uploadToSuperAnnotate,
  uploadVideoToSpatialLabelingApp,
  VideoFilterPayload,
  VideoPropertyPayload
} from './actionCreators'

export const fetchVideoSegmentsFilterFlow = (
  page = 1,
  payload: VideoFilterPayload = {},
) => (dispatch: Dispatch) => {
  dispatch(fetchVideoSegmentsWithFilter.request(payload))
  axios
    .get(`/video/filter?${stringify({ page, ...payload })}`)
    .then(res => {
      dispatch(fetchVideoSegmentsWithFilter.success(res))
    })
    .catch(err => dispatch(fetchVideoSegmentsWithFilter.failure(err)))
}

export const fetchVideoBatchesFilterFlow = (
  page = 1,
  payload: VideoFilterPayload = {},
  numOfSentVideos = 0,
) => (dispatch: Dispatch) => {
  dispatch(fetchVideoBatchesWithFilter.request(payload))
  axios
    .get(`/video/batches?${stringify({ page, ...payload, numOfSentVideos })}`)
    .then(res => {
      dispatch(fetchVideoBatchesWithFilter.success(res))
    })
    .catch(err => dispatch(fetchVideoBatchesWithFilter.failure(err)))
}

export const fetchPublicVideoURLFlow = (
  params: Partial<ExtractFramesPayload>,
  videoId: string,
  batch: string,
) => (dispatch: Dispatch) => {
  dispatch(fetchPublicVideoURL.request(params))
  axios
    .get(`/video/get-public-url?${stringify(params)}`)
    .then(res => {
      const resPayload = {
        publicURL: get(res, ['data', 'publicURL'], ''),
        videoId,
        batch,
      }
      dispatch(fetchPublicVideoURL.success(resPayload))
    })
    .catch(err => dispatch(fetchPublicVideoURL.failure(err)))
}

export const extractFramesFlow = (params: any) => (dispatch: Dispatch) => {
  dispatch(extractFrames.request(params))
  axios
    .get(`/video/extract-frames?${stringify(params)}`)
    .then(res => {
      dispatch(extractFrames.success(res.data))
      showModalFlow('SUCCESS', {
        isOpen: true,
        message: 'Video exported to Darwin successfully!',
      })(dispatch)
    })
    .catch(err => {
      dispatch(extractFrames.failure(err))
      const error = parseHTTPError(err)
      showModalFlow('ERROR', {
        isOpen: true,
        message: error.message,
        status: error.status,
      })(dispatch)
    })
}

export const extractToDarwinFlow = (params: any) => (dispatch: Dispatch) => {
  dispatch(uploadToDarwin.request(params))
  axios
    .post("/video/upload-to-darwin", params)
    .then(res => {
      dispatch(uploadToDarwin.success(res.data))
      const videoURI = params.videoURI.split('/')
      const fileName = videoURI.pop()
      showModalFlow('SUCCESS', {
        isOpen: true,
        message: `${fileName} exported to Darwin successfully!`,
      })(dispatch)
    })
    .catch(err => {
      dispatch(uploadToDarwin.failure(err))
      const error = parseHTTPError(err)
      showModalFlow('ERROR', {
        isOpen: true,
        message: error.message,
        status: error.status,
      })(dispatch)
    })
}

export const uploadDicomsToEncordFlow =
  (
    dicomURIs: string[],
    destinationFolderEncord: string,
    commonSeriesDescription: string,
    videoTags: string[],
    videoDoctor: string,
    videoRegion: string,
    radiologyReportText: string
  ) =>
  (dispatch: Dispatch) => {
    const payload = {
      input_files_presigned_urls: dicomURIs,
      destinationFolderEncord,
      commonSeriesDescription,
      videoTags,
      videoDoctor,
      videoRegion,
      radiologyReportText,
      asyncUpload: true,
    };

    dispatch(uploadDicomsToEncord.request(payload));

    axios
      .post("/video/upload-dicoms-to-encord", payload)
      .then((response) => {
        if (!response.data.task_id) {
          // Compatibility with a backend that has not yet been updated during
          // a rolling deployment and still returns the completed result.
          const status = _.get(response, 'data.status', '')
          const dicomName = _.get(response, 'data.dicomName', '')
          if (status === 'DONE' || status === 'PENDING') {
            dispatch(uploadDicomsToEncord.success(response.data))
            showModalFlow('SUCCESS', {
              isOpen: true,
              message: `DICOMS uploaded to Encord successfully! The file name is ${dicomName}.`,
            })(dispatch)
            return
          }

          const message = _.get(
            response,
            'data.message',
            'DICOM export returned an unexpected response',
          )
          dispatch(uploadDicomsToEncord.failure({
            response: { data: { message }, status: response.status },
          } as any))
          showModalFlow('ERROR', {
            isOpen: true,
            message,
            status: response.status,
          })(dispatch)
          return
        }

        pollUploadTaskStatus(
          response.data.task_id,
          dispatch,
          responseBody => {
            const dicomName = _.get(responseBody, 'dicomName', '')
            dispatch(uploadDicomsToEncord.success(responseBody))
            showModalFlow('SUCCESS', {
              isOpen: true,
              message: `DICOMS uploaded to Encord successfully! The file name is ${dicomName}.`,
            })(dispatch)
          },
          (responseBody, responseCode) => {
            const message = responseBody?.message || 'Upload failed due to an error.'
            dispatch(uploadDicomsToEncord.failure({
              response: { data: { ...responseBody, message }, status: responseCode || 500 },
            } as any))
            showModalFlow('ERROR', {
              isOpen: true,
              message,
              status: responseCode || 500,
            })(dispatch)
          },
          'dicom',
        )
      })
      .catch((err) => {
        dispatch(uploadDicomsToEncord.failure(err));

        const error = parseHTTPError(err);

        showModalFlow("ERROR", {
          isOpen: true,
          message: error.message,
          status: error.status,
        })(dispatch);
      });
  };


export const uploadDicomAsMP4Flow = (
  dicomURIs: string[],
  destinationProjectSuperAnnotate: string,
  commonSeriesDescription: string,
  videoTags: string[],
  videoDoctor: string,
  videoRegion: string
) => (dispatch: Dispatch) => {
  const payload = {
    input_files_presigned_urls: dicomURIs,
    destinationProjectSuperAnnotate: destinationProjectSuperAnnotate,
    commonSeriesDescription: commonSeriesDescription,
    videoTags: videoTags,
    videoDoctor: videoDoctor,
    videoRegion: videoRegion,
  };

  dispatch(uploadDicomAsMP4.request(payload));

  axios
    .post("/video/upload-dicoms-as-mp4", payload)
    .then((response) => {
      const isSuccess = _.get(
        response,
        "data.isDicomUploadedAsMP4Successfully",
        false
      );
      const outputVideoName = _.get(response, "data.outputVideoName", "");

      dispatch(uploadDicomAsMP4.success(response.data));

      if (isSuccess) {
        showModalFlow("SUCCESS", {
          isOpen: true,
          message: `Dicoms uploaded as .mp4 successfully! The video name is ${outputVideoName}.`,
        })(dispatch);
      } else {
        showModalFlow("ERROR", {
          isOpen: true,
          message: "Dicom not uploaded as mp4 file",
        })(dispatch);
      }
    })
    .catch((err) => {
      dispatch(uploadDicomAsMP4.failure(err));
      const error = parseHTTPError(err);
      showModalFlow("ERROR", {
        isOpen: true,
        message: error.message,
        status: error.status,
      })(dispatch);
    });
};

export const uploadDicomsToFlexViewFlow = (
  dicomURIs: string[],
  metadata: Partial<FileItem> | null
) => (dispatch: Dispatch) => {
  const payload = {
    dicomURIs,
    metadata: metadata || {},
  };

  dispatch(uploadDicomsToFlexView.request(payload));

  axios
    .post("/video/upload-dicoms-to-flexview", payload)
    .then((response) => {
      const isSuccess = _.get(
        response,
        "data.isDicomsUploadedToFlexViewSuccessfully",
        false
      );

      dispatch(uploadDicomsToFlexView.success(response.data));

      if (isSuccess) {
        const metadataDetails = `
          <h3 style="color: #007bff; font-size: 18px; margin-bottom: 10px;">Study Level Information</h3>
          <p>Patient Name: ${metadata?.patientName || "N/A"}</p>
          <p>MRN: ${metadata?.patientID || "N/A"}</p>
          <p>Study Date: ${metadata?.studyDate || "N/A"}</p>
          <p>Study Description: ${metadata?.studyDescription || "N/A"}</p>
          <p>Accession Number: ${metadata?.accessionNumber || "N/A"}</p>
          
          <h3 style="color: #007bff; font-size: 18px; margin-top: 20px; margin-bottom: 10px;">Series Level Information</h3>
          <p>Series Description: ${metadata?.seriesDescription || "N/A"}</p>
          <p>Series Number: ${metadata?.seriesNumber || "N/A"}</p>
          <p>Modality: ${metadata?.modality || "N/A"}</p>
          <p>Number of Instances: ${metadata?.numStudyInstances || "N/A"}</p>
        `;
      
        showModalFlow("SUCCESS", {
          isOpen: true,
          message: `
            <div style="margin-bottom: 10px; font-size: 18px; color: #333;">
              Dicoms uploaded to FlexView successfully!
            </div>
            ${metadataDetails}
            <div style="margin-top: 10px; font-size: 18px;">
              Please login to <a href="https://app.flexview.ai/login/" 
              target="_blank" 
              rel="noopener noreferrer" 
              style="color: #007bff; font-weight: bold; text-decoration: underline;">FlexView</a> to view them.
            </div>
          `,
        })(dispatch);
      }
       else {
        showModalFlow("ERROR", {
          isOpen: true,
          message: "Failed to upload DICOMs to FlexView.",
        })(dispatch);
      }
    })
    .catch((err) => {
      dispatch(uploadDicomsToFlexView.failure(err));
      const error = parseHTTPError(err);
      showModalFlow("ERROR", {
        isOpen: true,
        message: error.message,
        status: error.status,
      })(dispatch);
    });
};

export const uploadToSuperAnnotateFlow =
  (params: any) => (dispatch: Dispatch) => {
    dispatch(uploadToSuperAnnotate.request(params))
    axios
      .post("/video/upload-to-super-annotate", params)
      .then(res => {
        dispatch(uploadToSuperAnnotate.success(res.data))
        const videoURI = params.videoURI.split('/')
        const fileName = videoURI.pop()
        showModalFlow('SUCCESS', {
          isOpen: true,
          message: `${fileName} exported to Super Annotate successfully!`,
        })(dispatch)
      })
      .catch(err => {
        dispatch(uploadToSuperAnnotate.failure(err))
        const error = parseHTTPError(err)
        showModalFlow('ERROR', {
          isOpen: true,
          message: error.message,
          status: error.status,
        })(dispatch)
      })
  }

const POLL_INITIAL_INTERVAL = 5000
const POLL_MAX_INTERVAL = 30000
const POLL_MAX_DURATION = 3600000 // 1 hour

export const encordUploadPhase = {
  single: 'queued',
  batch: 'queued',
  dicom: 'queued',
  singleStartedAt: 0,
  batchStartedAt: 0,
  dicomStartedAt: 0,
  singleFileName: '',
  singleReencode: false,
  singleTargetFps: null as string | null,
  batchVideoCount: 0,
  batchReencode: false,
  batchTargetFps: null as string | null,
}

const pollUploadTaskStatus = (
  taskId: string,
  dispatch: Dispatch,
  onComplete: (responseBody: any) => void,
  onFailed: (responseBody: any, responseCode: number) => void,
  phaseKey: 'single' | 'batch' | 'dicom' = 'single',
) => {
  let currentInterval = POLL_INITIAL_INTERVAL
  let consecutivePollErrors = 0
  const startTime = Date.now()
  encordUploadPhase[phaseKey] = 'queued'
  if (phaseKey === 'single') {
    encordUploadPhase.singleStartedAt = Date.now()
  } else if (phaseKey === 'batch') {
    encordUploadPhase.batchStartedAt = Date.now()
  } else {
    encordUploadPhase.dicomStartedAt = Date.now()
  }

  const poll = () => {
    if (Date.now() - startTime > POLL_MAX_DURATION) {
      onFailed({ message: 'Upload timed out. Please check the Encord dashboard for status.' }, 504)
      return
    }

    axios.get(`/video/upload-task-status/${taskId}`)
      .then(res => {
        consecutivePollErrors = 0
        const {
          status,
          phase,
          response_body = {},
          response_code = 500,
        } = res.data

        if (phase) {
          encordUploadPhase[phaseKey] = phase
        }

        if (status === 'processing') {
          setTimeout(poll, currentInterval)
          currentInterval = Math.min(currentInterval * 1.5, POLL_MAX_INTERVAL)
          return
        }

        encordUploadPhase[phaseKey] = 'done'
        if (status === 'complete') {
          onComplete(response_body)
        } else {
          onFailed(response_body, response_code)
        }
      })
      .catch(() => {
        consecutivePollErrors++
        if (consecutivePollErrors >= 3) {
          onFailed({ message: 'Failed to check upload status. Please check the Encord dashboard.' }, 500)
        } else {
          setTimeout(poll, currentInterval)
        }
      })
  }

  setTimeout(poll, POLL_INITIAL_INTERVAL)
}

export const uploadToEncordFlow = (params: any) => (dispatch: Dispatch) => {
  dispatch(uploadToEncord.request(params))
  const videoURIParts = params.videoURI.split('/')
  encordUploadPhase.singleFileName = videoURIParts[videoURIParts.length - 1] || ''
  encordUploadPhase.singleReencode = params.reencode ?? true
  encordUploadPhase.singleTargetFps = params.targetFps != null ? String(params.targetFps) : null
  const { _videoId, _exportCount, _batch, _logPayload, ...restPayload } = params
  const apiPayload = {
    ...restPayload,
    videoId: _videoId,
    exportCount: _exportCount,
    videoBatch: _batch,
    logPayload: _logPayload,
  }
  axios
    .post("/video/upload-to-encord", apiPayload)
    .then(res => {
      if (!res.data.task_id) {
        throw new Error('Server did not return a task ID for upload tracking')
      }
      pollUploadTaskStatus(
        res.data.task_id,
        dispatch,
        (responseBody) => {
          dispatch(uploadToEncord.success(responseBody))
          const videoURIParts = params.videoURI.split('/')
          const fileName = videoURIParts.pop()
          if (_videoId) {
            editVideoCountFlow({
              videoId: _videoId,
              export_count: (_exportCount || 0) + 1,
              batch: _batch,
            })(dispatch)
          }
          Toaster.show({
            icon: 'tick',
            intent: Intent.SUCCESS,
            message: `${fileName} exported to Encord successfully!`,
            timeout: 0,
          })
        },
        (responseBody, responseCode) => {
          dispatch(uploadToEncord.failure({
            response: { data: responseBody, status: responseCode }
          } as any))
          Toaster.show({
            icon: 'cross',
            intent: Intent.DANGER,
            message: responseBody.message || 'An error occurred during export.',
            timeout: 0,
          })
        },
        'single',
      )
    })
    .catch(err => {
      dispatch(uploadToEncord.failure(err))
      const error = parseHTTPError(err)
      Toaster.show({
        icon: 'cross',
        intent: Intent.DANGER,
        message: error.message,
        timeout: 0,
      })
    })
}

export const archiveEncordAssetsFlow = (params: any) => (dispatch: Dispatch) => {
  dispatch(archiveEncordAssets.request(params));
  
  axios
    .post("/video/archive-encord-assets", params)
    .then(res => {
      dispatch(archiveEncordAssets.success(res.data));
      
      const summaryHTML = `
        <h3 style="color: #007bff; font-size: 18px; margin-bottom: 10px;">Archive Summary</h3>
        <p>Assets Processed: <strong>${res.data.processed || 0}</strong></p>
        <p>Assets Archived: <strong>${res.data.archived || 0}</strong></p>
        <p>Assets sent to next stage: <strong>${res.data.reviewed || 0}</strong></p>
        <p>Errors Encountered: <strong>${res.data.errors || 0}</strong></p>
      `;
      
      const formatFileList = (files = []) => {
        if (!files.length) return '<p><em>None</em></p>';
        
        const maxHeight = files.length > 10 ? 'max-height: 300px; overflow-y: auto;' : '';
        
        let html = `<ol style="margin-top: 5px; margin-bottom: 5px; padding-left: 25px; ${maxHeight}">`;
        files.forEach(file => {
          html += `<li style="margin-bottom: 3px;">${file}</li>`;
        });
        html += '</ol>';
        
        return html;
      };
      
      const fileListsHTML = `
        <div style="margin-top: 20px;">
          ${(res.data.archived_files?.length || 0) > 0 ? `
            <div style="margin-bottom: 15px;">
              <h4 style="color: #28a745; font-size: 16px; margin-bottom: 5px;">Archived Assets (${res.data.archived_files?.length || 0})</h4>
              ${formatFileList(res.data.archived_files)}
            </div>
          ` : ''}
          
          ${(res.data.reviewed_files?.length || 0) > 0 ? `
            <div>
              <h4 style="color: #17a2b8; font-size: 16px; margin-bottom: 5px;">Assets sent to next stage (${res.data.reviewed_files?.length || 0})</h4>
              ${formatFileList(res.data.reviewed_files)}
            </div>
          ` : ''}
        </div>
      `;
      
      const noAssetsProcessed = (res.data.processed || 0) === 0;
      
      if (res.data.status === 'success') {
        showModalFlow('SUCCESS', {
          isOpen: true,
          message: `
            <div style="margin-bottom: 10px; font-size: 18px; color: #333;">
              ${noAssetsProcessed ? 'No assets required processing in Encord.' : 'Assets archived in Encord successfully!'}
            </div>
            ${summaryHTML}
            ${(res.data.archived || 0) > 0 || (res.data.reviewed || 0) > 0 ? fileListsHTML : ''}
            <div style="margin-top: 15px; font-size: 14px; color: #666;">
              Project ID: ${params.projectId}
            </div>
          `
        })(dispatch);
      } 
    })
    .catch(err => {
      dispatch(archiveEncordAssets.failure(err));
      
      showModalFlow("ERROR", {
        isOpen: true,
        message: "Failed to archive assets in Encord. Please enter the correct Project ID."
      })(dispatch);
    });
}

export const batchUploadToEncordFlow = (params: any) => (dispatch: Dispatch) => {
  dispatch(batchUploadToEncord.request(params))
  encordUploadPhase.batchVideoCount = params._videoIDs?.length || 0
  encordUploadPhase.batchReencode = params.reencode ?? true
  encordUploadPhase.batchTargetFps = params.targetFps != null ? String(params.targetFps) : null
  const { _videoIDs, _videoExportCounts, _videoBatches, _logPayloads, ...restPayload } = params
  const apiPayload = {
    ...restPayload,
    videoIds: _videoIDs,
    videoExportCounts: _videoExportCounts,
    videoBatches: _videoBatches,
    logPayloads: _logPayloads,
  }
  axios
    .post("/video/batch-upload-to-encord", apiPayload)
    .then(res => {
      if (!res.data.task_id) {
        throw new Error('Server did not return a task ID for batch upload tracking')
      }
      pollUploadTaskStatus(
        res.data.task_id,
        dispatch,
        (responseBody) => {
          dispatch(batchUploadToEncord.success(responseBody))
          Toaster.show({
            icon: 'tick',
            intent: Intent.SUCCESS,
            message: 'All videos exported to Encord successfully!',
            timeout: 0,
          })
        },
        (responseBody, responseCode) => {
          dispatch(batchUploadToEncord.failure({
            response: { data: responseBody, status: responseCode }
          } as any))
          Toaster.show({
            icon: 'cross',
            intent: Intent.DANGER,
            message: responseBody.message || 'An error occurred during batch export.',
            timeout: 0,
          })
        },
        'batch',
      )
    })
    .catch(err => {
      dispatch(batchUploadToEncord.failure(err))
      const error = parseHTTPError(err)
      Toaster.show({
        icon: 'cross',
        intent: Intent.DANGER,
        message: error.message,
        timeout: 0,
      })
    })
}

export const getSpatialLabelingAPITokenFlow = () => {
  return (dispatch: Dispatch) => {
    dispatch(getSpatialLabelingAPIToken.request())

    return axios
      .post('/video/get-spatial-labeling-api-token')
      .then(res => {
        const { access_token } = res.data
        dispatch(getSpatialLabelingAPIToken.success({ access_token }))
        return access_token
      })
      .catch(err => {
        dispatch(getSpatialLabelingAPIToken.failure(err))
        const error = parseHTTPError(err)

        showModalFlow('ERROR', {
          isOpen: true,
          message: error.message,
          status: error.status,
        })(dispatch)

        throw error
      })
  }
}

export const uploadVideoToSpatialLabelingAppFlow =
  (
    accessToken: (dispatch: Dispatch) => Promise<any>,
    projectName: string,
    videoName: string,
    videoURI: string,
    fps: number,
    region: string,
    surgeon: string,
    videoTags: string[]
  ) =>
  (dispatch: Dispatch): Promise<any> => {
    dispatch(uploadVideoToSpatialLabelingApp.request())

    const payload = {
      accessToken,
      projectName,
      videoName,
      videoURI,
      fps,
      region,
      surgeon,
      videoTags
    }

    return axios
      .post('/video/upload-video-to-spatial-labeling-app', payload)
      .then(res => {
        dispatch(uploadVideoToSpatialLabelingApp.success(res.data))

        showModalFlow('SUCCESS', {
          isOpen: true,
          message: `Video exported to Spatial Labeling App successfully!`,
        })(dispatch)

        return res.data
      })
      .catch(err => {
        dispatch(uploadVideoToSpatialLabelingApp.failure(err))

        const error = parseHTTPError(err)

        showModalFlow('ERROR', {
          isOpen: true,
          message: error.message,
          status: error.status,
        })(dispatch)

        return Promise.reject(error)
      })
  }

export const batchUploadToSuperAnnotateFlow = (params: any) => (
    dispatch: Dispatch
  ) => {
    dispatch(batchUploadToSuperAnnotate.request(params))
    axios
      .post("/video/batch-upload-to-super-annotate", params)
      .then(res => {
        dispatch(batchUploadToSuperAnnotate.success(res.data))
        showModalFlow('SUCCESS', {
          isOpen: true,
          message: 'Videos exported to Super Annotate successfully!',
        })(dispatch)
      })
      .catch(err => {
        dispatch(batchUploadToSuperAnnotate.failure(err))
        const error = parseHTTPError(err)
        showModalFlow('ERROR', {
          isOpen: true,
          message: error.message,
          status: error.status,
        })(dispatch)
      })
  }

export const batchUploadToDarwinFlow = (params: any) => (
  dispatch: Dispatch
) => {
  dispatch(batchUploadToDarwin.request(params))
  axios
    .post("/video/batch-upload-to-darwin", params)
    .then(res => {
      dispatch(batchUploadToDarwin.success(res.data))
      showModalFlow('SUCCESS', {
        isOpen: true,
        message: 'Videos exported to Darwin successfully!',
      })(dispatch)
    })
    .catch(err => {
      dispatch(batchUploadToDarwin.failure(err))
      const error = parseHTTPError(err)
      showModalFlow('ERROR', {
        isOpen: true,
        message: error.message,
        status: error.status,
      })(dispatch)
    })
}

export const getVideoFramesFlow = (
  params: ExtractFramesPayload,
  currUserId: string,
  // setActiveTab: React.Dispatch<React.SetStateAction<string>>,
) => (dispatch: Dispatch) => {
  dispatch(extractVideoFrames.request(params))
  axios
    .get(`/video/get-frames?${stringify(params)}`)
    .then(res => {
      dispatch(
        extractVideoFrames.success({
          videoId: params.videoId,
          frames: res.data['frames'],
        }),
      )
      // setActiveTab('0')
      addVideoHistoryEntryFlow({
        userId: currUserId,
        videoId: params.videoId,
        action: `extracted frames of time range ${getTimeRangeString(
          params.startTime,
          params.endTime,
        )}`,
        type: EntryType.EXTRACT_FRAMES,
      })(dispatch)
    })
    .catch(err => {
      dispatch(extractVideoFrames.failure(err))
      const error = parseHTTPError(err)
      showModalFlow('ERROR', {
        isOpen: true,
        message: error.message,
        status: error.status,
      })(dispatch)
    })
}

export const editVideoPropertyFlow = (
  payload: VideoPropertyPayload,
  currUserId: string,
  dateRange: { startDate: string; endDate: string },
  property: PropertyType,
  oldValue = '',
  newValue = '',
) => (dispatch: Dispatch) => {
  dispatch(editVideoProperty.request(payload))
  axios
    .patch('/video/property', payload)
    .then(res => {
      dispatch(editVideoProperty.success(res))
      getVideoFilterFieldsFlow(dateRange)(dispatch)
      const type = getHistoryTypeFromRegion(property)
      const action = getHistoryActionFromRegion(type, oldValue, newValue)
      addVideoHistoryEntryFlow({
        userId: currUserId,
        videoId: payload.videoId,
        action,
        type,
      })(dispatch)
    })
    .catch(err => dispatch(editVideoProperty.failure(err)))
}

export const editVideoTagFlow = (
  payload: VideoPropertyPayload,
  currUserId: string,
  dateRange: { startDate: string; endDate: string },
) => (dispatch: Dispatch) => {
  dispatch(editVideoTag.request(payload))
  axios
    .patch('/video/property', payload)
    .then(res => {
      dispatch(editVideoTag.success(res))
      getVideoFilterFieldsFlow(dateRange)(dispatch)
      /*
      addVideoHistoryEntryFlow({
        userId: currUserId,
        videoId: payload.videoId,
        action: getHistoryActionFromTagChange(type, tag),
        type,
      })(dispatch)
      */
    })
    .catch(err => dispatch(editVideoTag.failure(err)))
}

export const editVideoTagFlowIndiv = (
  payload: VideoPropertyPayload,
  currUserId: string,
  dateRange: { startDate: string; endDate: string },
) => (dispatch: Dispatch) => {
  dispatch(editVideoProperty.request(payload))
  axios
    .patch('/video/property', payload)
    .then(res => {
      dispatch(editVideoProperty.success(res))
      getVideoFilterFieldsFlow(dateRange)(dispatch)
      /*
      addVideoHistoryEntryFlow({
        userId: currUserId,
        videoId: payload.videoId,
        action: getHistoryActionFromTagChange(type, tag),
        type,
      })(dispatch)
      */
    })
    .catch(err => dispatch(editVideoProperty.failure(err)))
}

export const editVideoCountFlow = (payload: VideoPropertyPayload) => (
  dispatch: Dispatch,
) => {
  dispatch(editVideoProperty.request(payload))
  axios
    .patch('/video/property', payload)
    .then(res => {
      dispatch(editVideoProperty.success(res))
    })
    .catch(err => dispatch(editVideoProperty.failure(err)))
}

export const addVideoImportLogFlow = (payload: any) => (dispatch: Dispatch) => {
  dispatch(addVideoImportLog.request(payload))
  axios
    .post('/video/logs', payload)
    .then(res => {
      dispatch(addVideoImportLog.success(res))
    })
    .catch(err => dispatch(addVideoImportLog.failure(err)))
}

export const getVideoFilterFieldsFlow = (payload: VideoFilterPayload = {}) => (
  dispatch: Dispatch,
) => {
  dispatch(getFilterFields.request(payload))
  axios
    .get(`/video/filter-fields?${stringify(payload)}`)
    .then(res => {
      const filterFields: FilterFields = res.data
      dispatch(getFilterFields.success(filterFields))
    })
    .catch(err => dispatch(getFilterFields.failure(err)))
}

export const bulkAddTagsFlow = (payload: {
  videoIds: string[]
  tags: string[]
  classificationTags: string[]
}) => (dispatch: Dispatch) => {
  dispatch(bulkAddTags.request(payload))
  axios
    .patch('/video/tag/bulk', payload)
    .then(() => {
      dispatch(bulkAddTags.success(payload))
      showModalFlow('SUCCESS', {
        isOpen: true,
        message: 'Added tags successfully!',
      })(dispatch)
    })
    .catch(err => {
      dispatch(bulkAddTags.failure(err))
      showModalFlow('ERROR', {
        isOpen: true,
        message: 'Failed to add tags',
        status: 500,
      })(dispatch)
    })
}

export const bulkAddRegionFlow = (payload: {
  videoIds: string[]
  region: string
}) => (dispatch: Dispatch) => {
  dispatch(bulkAddRegion.request(payload))
  axios
    .patch('/video/region/bulk', payload)
    .then(() => {
      dispatch(bulkAddRegion.success(payload))
      showModalFlow('SUCCESS', {
        isOpen: true,
        message: 'Added region successfully!',
      })(dispatch)
    })
    .catch(err => {
      dispatch(bulkAddRegion.failure(err))
      showModalFlow('ERROR', {
        isOpen: true,
        message: 'Failed to add region',
        status: 500,
      })(dispatch)
    })
}
